/**
 * The red door.
 *
 * A cook was pressing the X on a comanda thinking it meant "done, next" and
 * cancelling live orders. So the X opens this instead of a browser confirm: red
 * frame, the consequences spelled out, and the safe button — go back — as the
 * primary one holding focus. Cancelling is deliberately the secondary action.
 */
import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Trans, useTranslation } from "../i18n";

interface CancelOrderDialogProps {
  order: any;
  itemCount: number;
  onConfirm: () => Promise<void>;
  onDismiss: () => void;
}

export default function CancelOrderDialog({
  order,
  itemCount,
  onConfirm,
  onDismiss,
}: CancelOrderDialogProps) {
  const { t } = useTranslation();
  const [inFlight, setInFlight] = useState(false);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-order-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !inFlight) onDismiss();
      }}
    >
      <div
        className="bg-kds-card border-4 border-red-600 rounded-xl max-w-xl w-full shadow-2xl shadow-red-600/40 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-red-600 text-white px-6 py-5 flex items-center gap-4">
          <AlertTriangle size={44} className="shrink-0" />
          <div>
            <h2 id="cancel-order-title" className="text-kds-title uppercase tracking-wide leading-tight">
              {t("kds.cancel.title", "Annullare l'ordine?")}
            </h2>
            <p className="text-kds-meta text-red-100 mt-1">
              {t("kds.cancel.subtitle", "Ordine #{{number}} · {{count}} piatti", {
                number: order?.dailyNumber ?? order?.id?.slice(0, 6),
                count: itemCount,
              })}
            </p>
          </div>
        </div>

        <div className="px-6 py-5 text-kds-text">
          <p className="text-kds-action text-red-300 mb-4">{t("kds.cancel.final", "Questa azione è DEFINITIVA.")}</p>
          <ul className="space-y-3 text-kds-note">
            <li className="flex items-start gap-3">
              <span className="text-red-400 font-bold">✕</span>
              <span>
                <Trans
                  i18nKey="kds.cancel.noStockUnload"
                  defaults="I piatti <b>NON</b> verranno scaricati dal magazzino"
                  components={{ b: <b /> }}
                />
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-red-400 font-bold">✕</span>
              <span>
                <Trans
                  i18nKey="kds.cancel.notBilled"
                  defaults="Il tavolo <b>NON</b> verrà fatturato per questi piatti"
                  components={{ b: <b /> }}
                />
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-red-400 font-bold">✕</span>
              <span>{t("kds.cancel.leavesKitchen", "L'ordine sparirà dalla cucina")}</span>
            </li>
          </ul>
          <div className="mt-5 bg-yellow-900/40 border-l-4 border-yellow-500 px-4 py-3 text-kds-meta text-yellow-100">
            <b>{t("kds.cancel.useComplete", 'Per chiudere un piatto pronto premi "Completa", NON la X.')}</b>
            <br />
            {t("kds.cancel.onlyWrongOrders", 'Usa "Annulla" SOLO per ordini sbagliati o fantasma.')}
          </div>
        </div>

        <div className="px-6 py-5 bg-kds-bg flex gap-4">
          <button
            type="button"
            autoFocus
            onClick={onDismiss}
            disabled={inFlight}
            className="flex-1 bg-kds-ready hover:bg-kds-ready/90 text-white rounded-lg px-6 py-4 text-kds-action min-h-touch transition-colors disabled:opacity-50"
          >
            {t("kds.cancel.goBack", "← Torna indietro")}
          </button>
          <button
            type="button"
            disabled={inFlight}
            onClick={async () => {
              setInFlight(true);
              try {
                await onConfirm();
                onDismiss();
              } catch (err: any) {
                alert(err?.message || t("kds.cancel.failed", "Cancellazione fallita"));
              } finally {
                setInFlight(false);
              }
            }}
            className="bg-red-700 hover:bg-red-600 text-white rounded-lg px-6 py-4 text-kds-meta min-h-touch transition-colors disabled:opacity-50 border-2 border-red-500/60"
          >
            {inFlight ? t("kds.cancel.cancelling", "Annullamento…") : t("kds.cancel.confirm", "Sì, annulla ordine")}
          </button>
        </div>
      </div>
    </div>
  );
}
